<template>
  <div style="position: relative">
    <div id="gmap" :style="{ width: mapWidth, height: mapHeight }"></div>
    <div class="mapInfo" v-if="hasMapInfo">
      <v-menu bottom offset-y v-model="menu">
        <template v-slot:activator="{ attrs }">
          <v-btn color="cyan darken-2" dark x-small v-bind="attrs" depressed fab v-on="{ ...menu }"
            @click="menu = true">
            <v-icon v-if="!menu">mdi-information</v-icon>
            <v-icon v-else>mdi-close</v-icon>
          </v-btn>
        </template>
        <v-card>
          <slot name="mapInfo" />
        </v-card>
      </v-menu>
    </div>


    <div class="fullscreen-toggle">
      <v-btn color="white" x-small depressed fab @click="toggleFullscreen"
        :title="isFullscreen ? 'Exit Fullscreen' : 'Enter Fullscreen'">
        <v-icon color="primary">
          {{ isFullscreen ? 'mdi-fullscreen-exit' : 'mdi-fullscreen' }}
        </v-icon>
      </v-btn>
    </div>
  </div>
</template>

<script>
import {
  sourceMarker,
  destinationMarker,
  midMarker,
  defaultMarker,
  truckMarker,
  blueDot,
} from "@/assets/images/mapIcons.js";

export default {
  props: {
    isDrawable: {
      type: Boolean,
      default: false,
    },
    mapWidth: {
      type: String,
      default: "auto",
    },
    mapHeight: {
      type: String,
      default: "auto",
    },
    hasMapInfo: {
      type: Boolean,
      default: false,
    },
    markers: {
      type: Array,
      default: () => [],
    },
    enableClustering: {
      type: Boolean,
      default: true,
    },
  },
  data() {
    return {
      fab: false,
      menu: false,
      map: null,
      bounds: null,
      directionsRenderer: null,
       isFullscreen: false,
      directionsService: null,
      drawingManager: null,
      markerClusterer: null,
      lat: 24.4539,
      lng: 54.3773,
      polyOptions: {
        strokeColor: "#FF0000",
        strokeOpacity: 0.8,
        strokeWeight: 2,
        fillColor: "#00FF00",
        fillOpacity: 0.35,
        editable: true,
      },
      selectedShape: null,
      markersArray: [],

      markerColors: {},
    };
  },
  watch: {
    markers: {
      handler(newMarkers) {
        this.updateMarkers(newMarkers);
      },
      deep: true,
    },
  },
  methods: {
    handleFullscreenChange() {
      const isCurrentlyFullscreen =
        document.fullscreenElement ||
        document.webkitFullscreenElement ||
        document.mozFullScreenElement ||
        document.msFullscreenElement;

      this.isFullscreen = !!isCurrentlyFullscreen;

      if (this.map) {
        setTimeout(() => {
          google.maps.event.trigger(this.map, 'resize');
          if (this.markersArray.length > 0) {
            this.fitBoundsToMarkers();
          }
        }, 300);
      }
    },
    toggleFullscreen() {
      const mapContainer = this.$el;

      if (!this.isFullscreen) {
        // Enter fullscreen
        if (mapContainer.requestFullscreen) {
          mapContainer.requestFullscreen();
        } else if (mapContainer.webkitRequestFullscreen) {
          mapContainer.webkitRequestFullscreen();
        } else if (mapContainer.mozRequestFullScreen) {
          mapContainer.mozRequestFullScreen();
        } else if (mapContainer.msRequestFullscreen) {
          mapContainer.msRequestFullscreen();
        }
        this.isFullscreen = true;
      } else {

        if (document.exitFullscreen) {
          document.exitFullscreen();
        } else if (document.webkitExitFullscreen) {
          document.webkitExitFullscreen();
        } else if (document.mozCancelFullScreen) {
          document.mozCancelFullScreen();
        } else if (document.msExitFullscreen) {
          document.msExitFullscreen();
        }
        this.isFullscreen = false;
      }


      setTimeout(() => {
        if (this.map) {
          google.maps.event.trigger(this.map, 'resize');
          if (this.markersArray.length > 0) {
            this.fitBoundsToMarkers();
          }
        }
      }, 300);
    },
    initMap(center = null) {
      let interval = setInterval(() => {
        if (
          window.google &&
          window.google.maps &&
          window.google.maps.Map &&
          window.google.maps.LatLng
        ) {
          clearInterval(interval);
          if (!center) {
            center = { lat: this.lat, lng: this.lng };
          }
          this.map = new google.maps.Map(document.getElementById("gmap"), {
            zoom: 10,
            center: center,
            disableDefaultUI: true,
            keepSpiderfied: true,
          });
          this.bounds = new google.maps.LatLngBounds();

          if (this.enableClustering) {
            this.initMarkerClusterer();
          }

          if (this.isDrawable) {
            this.drawingManager = new google.maps.drawing.DrawingManager({
              map: this.map,
              polygonOptions: this.polyOptions,
              rectangleOptions: this.polyOptions,
              drawingControl: true,
              drawingControlOptions: {
                position: google.maps.ControlPosition.RIGHT_TOP,
                editable: true,
                drawingModes: [
                  google.maps.drawing.OverlayType.POLYGON,
                  google.maps.drawing.OverlayType.RECTANGLE,
                ],
              },
            });
            google.maps.event.addListener(
              this.drawingManager,
              "overlaycomplete",
              this.overlayCompleteCallback.bind(this)
            );
          }

          if (this.markers.length > 0) {
            this.updateMarkers(this.markers);
          }
        }
      }, 100);
    },

    initMarkerClusterer() {
      let clusterInterval = setInterval(() => {
        if (this.map && (window.MarkerClusterer || window.markerClusterer)) {
          clearInterval(clusterInterval);

          const MarkerClustererClass =
            window.MarkerClusterer || window.markerClusterer;

          this.markerClusterer = new MarkerClustererClass(this.map, [], {
            gridSize: 60,
            maxZoom: 12,
            minimumClusterSize: 2,
          });
        }
      }, 100);
    },

    updateMarkers(markerData) {
      if (!this.map) return;

      this.removeAllMarkers();

      if (markerData && markerData.length > 0) {
        markerData.forEach((markerInfo) => {
          const marker = this.addMarker(markerInfo);
          this.markersArray.push(marker);
        });

        if (this.enableClustering && this.markerClusterer) {
          this.markerClusterer.clearMarkers();
          this.markerClusterer.addMarkers(this.markersArray);
        }

        this.fitBoundsToMarkers();
      }
    },

    fitBoundsToMarkers() {
      if (this.markersArray.length > 0) {
        const bounds = new google.maps.LatLngBounds();
        this.markersArray.forEach((marker) => {
          bounds.extend(marker.getPosition());
        });
        this.map.fitBounds(bounds);
      }
    },

    getDirections(options) {
      return new Promise((resolve, reject) => {
        let interval = setInterval(() => {
          if (window.google && window.google.maps && this.map) {
            clearInterval(interval);

            this.directionsService = new window.google.maps.DirectionsService();
            this.directionsService.route(options, (response, status) => {
              if (status !== "OK") {
                reject(new Error("Could not fetch directions."));
              }

              resolve(response);
            });
          }
        }, 500);
      });
    },

    generateGeoJsonFromLineString(lineString) {
      let geoJson = {
        features: [
          {
            geometry: lineString,
            properties: {},
            type: "Feature",
          },
        ],
        type: "FeatureCollection",
      };

      return geoJson;
    },

    generateLineStringFromRoute(path) {
      return new Promise((resolve, reject) => {
        let lineString = {
          coordinates: [],
          type: "LineString",
        };

        path.overview_path.forEach((p) => {
          lineString.coordinates.push([p.lng(), p.lat()]);
          if (path.overview_path.length == lineString.coordinates.length) {
            resolve(lineString);
          }
        });
      });
    },

    async removeGeoJson() {
      this.map.data.forEach((feature) => {
        this.map.data.remove(feature);
      });
    },

    async removeAllMarkers() {
      if (this.markerClusterer) {
        this.markerClusterer.clearMarkers();
      }

      while (this.markersArray.length != 0) {
        this.markersArray[0].setMap(null);
        this.markersArray.splice(0, 1);
      }
    },

    overlayCompleteCallback(event) {
      this.setSelection(event);
      if (event.type == "polygon") {
        window.google.maps.event.addListener(
          this.selectedShape.overlay.getPath(),
          "insert_at",
          () => {
            this.setSelection(event);
          }
        );
        window.google.maps.event.addListener(
          this.selectedShape.overlay.getPath(),
          "set_at",
          () => {
            this.setSelection(event);
          }
        );
      } else {
        window.google.maps.event.addListener(
          this.selectedShape.overlay,
          "bounds_changed",
          () => {
            this.setSelection.bind(this);
          }
        );
      }
      this.drawingManager.setDrawingMode(null);
      this.drawingManager.setOptions({
        drawingControl: false,
      });
    },

    setSelection(shape) {
      this.clearSelection();
      this.selectedShape = shape;
      if (shape.overlay.getEditable() == false) {
        shape.overlay.setEditable(true);
      }
      this.constructCordinates(this.selectedShape.type);
    },

    clearSelection() {
      if (this.selectedShape) {
        if (this.selectedShape.overlay) {
          this.selectedShape.overlay.setEditable(false);
        }
        this.selectedShape = null;
      }
    },

    constructCordinates(shapeType) {
      this.coordinates = [];
      if (shapeType == "polygon") {
        this.selectedShape.overlay
          .getPath()
          .getArray()
          .forEach((ele) => this.coordinates.push([ele.lng(), ele.lat()]));
        this.coordinates.push(this.coordinates[0]);
      } else {
        let bounds = this.selectedShape.overlay.getBounds();
        this.coordinates = [
          [bounds.getSouthWest().lng(), bounds.getSouthWest().lat()],
          [bounds.getNorthEast().lng(), bounds.getSouthWest().lat()],
          [bounds.getNorthEast().lng(), bounds.getNorthEast().lat()],
          [bounds.getSouthWest().lng(), bounds.getNorthEast().lat()],
          [bounds.getSouthWest().lng(), bounds.getSouthWest().lat()],
        ];
      }
    },

    async addGeoCollection(collection, reset = true) {
      if (reset) {
        await this.removeGeoJson();
      }
      this.map.data.addGeoJson(collection);
      this.map.data.setStyle(function (feature) {
        let color = feature.getProperty("actual_route") ? "red" : "black";
        return /** @type {!google.maps.Data.StyleOptions} */ ({
          strokeColor: color,
          strokeWeight: 2,
        });
      });
    },

    processPoints(geometry, callback, thisArg) {
      if (geometry instanceof google.maps.LatLng) {
        callback.call(thisArg, geometry);
      } else if (geometry instanceof google.maps.Data.Point) {
        callback.call(thisArg, geometry.get());
      } else {
        geometry.getArray().forEach((g) => {
          this.processPoints(g, callback, thisArg);
        });
      }
    },

    async addMultipleMarkers(coordinateList) {
      await this.removeAllMarkers();
      if (coordinateList.length) {
        let markers = [];
        coordinateList.forEach((coordinates) => {
          const marker = this.addMarker(coordinates);
          markers.push(marker);
          this.markersArray.push(marker);
        });

        if (this.enableClustering && this.markerClusterer) {
          this.markerClusterer.addMarkers(markers);
        }
      }
    },

    addMarker(coordinates, draggable = false) {
      let position = {};
      if (Array.isArray(coordinates)) {
        position = {
          lat: coordinates[1],
          lng: coordinates[0],
        };
      } else {
        position = {
          lat: coordinates.latitude || coordinates.position?.lat,
          lng: coordinates.longitude || coordinates.position?.lng,
        };
      }

      let markerConfig = {
        position: position,
        map: this.map,
        draggable: draggable,
        animation: google.maps.Animation.DROP,
      };

      if (coordinates.type || coordinates.color) {
        const iconUrl = this.getIcon(
          coordinates.type,
          coordinates.color,
          coordinates?.details?.fleet_number
        );
        if (iconUrl) {
          markerConfig.icon = {
            url: iconUrl,
            scaledSize: !coordinates.type
              ? new google.maps.Size(125, 65)
              : new google.maps.Size(40, 40),
          };
        }
      }

      let markerObj = new google.maps.Marker(markerConfig);

      if (coordinates.type == "truck" || coordinates.details) {
        this.addInfoWindow(markerObj, coordinates);
      }

      return markerObj;
    },

    addInfoWindow(marker, coordinates) {
      let infowindow = new google.maps.InfoWindow({
        maxWidth: 380,
        pixelOffset: new google.maps.Size(0, -10),
        disableAutoPan: false,
      });

      const infoWindowId = "info_" + Math.random().toString(36).substr(2, 9);

      let html = `
        <div class='enhanced-info-window'>
          <div class='info-header'>
            <div class='header-content'>
              <h3 class='info-title'>
                ${coordinates.title || "Trip Details"}
              </h3>
              ${coordinates.trip_id || coordinates.tripId || coordinates.id
          ? `<div class='trip-id'>Trip ID: ${coordinates.trip_id ||
          coordinates.tripId ||
          coordinates.id
          }</div>`
          : ""
        }
              ${coordinates.type === "truck"
          ? '<div class="truck-badge">🚛 Active Vehicle</div>'
          : ""
        }
            </div>
            <button class='close-button' onclick='closeInfoWindow_${infoWindowId}()' title='Close'>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <line x1="18" y1="6" x2="6" y2="18"></line>
                <line x1="6" y1="6" x2="18" y2="18"></line>
              </svg>
            </button>
          </div>
          
          <div class='info-content-scrollable'>
            <div class='info-content'>
      `;

      if (coordinates.details.fleet_number) {
        html += this.createDetailRow(
          "🔢",
          "Fleet Number",
          coordinates.details.fleet_number,
          "#FF9800"
        );
      }
      if (coordinates.details.gps_id) {
        html += this.createDetailRow(
          "🔢",
          "GPS ID",
          coordinates.details.gps_id,
          "#FF9800"
        );
      }
      if (coordinates.details.odometer_reading) {
        html += this.createDetailRow(
          "🔢",
          "Odometer Reading",
          coordinates.details.odometer_reading + " kms",
          "#FF9800"
        );
      }
      if (coordinates.details.trip_id) {
        html += this.createDetailRow(
          "📍",
          "Trip Id",
          coordinates.details.trip_id,
          "#008000"
        );
      }
      if (coordinates.details.vehicle_status) {
        html += this.createDetailRow(
          "📊",
          "Vehicle Status",
          coordinates.details.vehicle_status,
          coordinates.details.status_color
        );
      }
      if (coordinates.details.vehicle_type) {
        html += this.createDetailRow(
          "🚚",
          "Vehicle Type",
          coordinates.details.vehicle_type,
          "#008000"
        );
      }

      if (coordinates.details.status_duration) {
        html += this.createDetailRow(
          "⏰",
          "Status Duration",
          coordinates.details.status_duration,
          "#008000                                                                                            "
        );
      }

      if (coordinates.details.driver) {
        html += this.createDetailRow(
          "👤",
          "Driver",
          coordinates.details.driver,
          "#401ee9"
        );
      }
      // if (coordinates.details.fuel) {
      //   html += this.createDetailRow('📱', 'Fuel', coordinates.details.fuel,+ '%',  '#9C27B0');
      // }
      if (coordinates.details.speed) {
        html += this.createDetailRow(
          "⚡",
          "Speed",
          coordinates.details.speed + " km/h",
          "#E91E63"
        );
      }

      if (coordinates.details) {
        Object.keys(coordinates.details).forEach((key) => {
          const value = coordinates.details[key];
          const formattedKey = this.formatKey(key);
          // html += this.createDetailRow('•', formattedKey, value, '#607D8B');
        });
      }

      html += `
            </div>
          </div>
          <div class='info-footer'>
            <small class='timestamp'>Last updated: ${new Date().toLocaleTimeString()}</small>
          </div>
        </div>
      `;

      window[`closeInfoWindow_${infoWindowId}`] = () => {
        infowindow.close();
      };

      marker.addListener("click", () => {
        if (this.currentInfoWindow) {
          this.currentInfoWindow.close();
        }
        infowindow.setContent(html);
        infowindow.open(this.map, marker);
        this.currentInfoWindow = infowindow;
      });

      if (coordinates.type == "truck") {
        let hoverContent = `
          <div class='hover-info-window'>
            <div class='hover-title'>${coordinates.vehicle_id || "Vehicle"
          }</div>
            <div class='hover-speed'>Speed: ${coordinates.speed || coordinates.details?.speed || 0
          } km/h</div>
          </div>
        `;

        marker.addListener("mouseover", () => {
          const hoverWindow = new google.maps.InfoWindow({
            content: hoverContent,
            maxWidth: 200,
          });
          hoverWindow.open(this.map, marker);

          marker.hoverWindow = hoverWindow;
        });

        marker.addListener("mouseout", () => {
          if (marker.hoverWindow) {
            marker.hoverWindow.close();
          }
        });
      }
    },

    createDetailRow(icon, label, value, color = "#ff0000") {
      return `
        <div class='detail-row'>
          <div class='detail-icon' style='color: ${color}'>${icon}</div>
          <div class='detail-content'>
            <span class='detail-label'>${label}:</span>
            <span class='detail-value' style='color: ${color}; font-weight: 600;'>${value}</span>
          </div>
        </div>
      `;
    },

    formatKey(key) {
      return key
        .replace(/_/g, " ")
        .replace(/([A-Z])/g, " $1")
        .toLowerCase()
        .replace(/^./, (str) => str.toUpperCase());
    },

    getIcon(type, color, fleet_number) {
      if (type) {
        switch (type) {
          case "source":
            return sourceMarker;
          case "destination":
            return destinationMarker;
          case "mid":
            return midMarker;
          case "truck":
            return truckMarker;
          case "blue_dot":
            return blueDot;
          default:
            if (color) {
              return this.createPinMarker(color, fleet_number);
            }
            return defaultMarker;
        }
      }

      if (color) {
        return this.createPinMarker(color, fleet_number);
      }

      return defaultMarker;
    },

    createPinMarker(color, fleet_number) {
     

      const svg = `
<svg width="48" height="72" viewBox="0 0 48 72" xmlns="http://www.w3.org/2000/svg">
  <!-- Location Pin -->
  <g transform="translate(0, 2) scale(1.3)">
    <path 
      fill="${color}" 
      fill-rule="evenodd" 
      d="M23.906 3.994a8.002 8.002 0 0 1 8.09 8.421 7.996 7.996 0 0 1-1.297 3.957.996.996 0 0 1-.133.204l-.108.129c-.178.243-.37.477-.573.699l-5.112 6.224a1 1 0 0 1-1.545 0L17.982 17.26l-.002-.002a18.146 18.146 0 0 1-.309-.38l-.133-.163a.999.999 0 0 1-.13-.202 7.995 7.995 0 0 1 6.498-12.518ZM27 11.997a3 3 0 1 1-5.999 0 3 3 0 0 1 5.999 0Z" 
      clip-rule="evenodd"
    />
  </g>
  
  <!-- Fleet Number Text -->
  <text
    x="25"
    y="54"
    font-size="14"
    font-weight="bold"
    fill="red"
    text-anchor="middle"
    font-family="Arial, sans-serif"
  >
${fleet_number}
  </text>
</svg>`;

      return "data:image/svg+xml;charset=UTF-8," + encodeURIComponent(svg);
    },

    toggleClustering() {
      this.enableClustering = !this.enableClustering;

      if (this.enableClustering) {
        if (!this.markerClusterer) {
          this.initMarkerClusterer();
        }
        if (this.markerClusterer) {
          this.markerClusterer.addMarkers(this.markersArray);
        }
      } else {
        if (this.markerClusterer) {
          this.markerClusterer.clearMarkers();
        }
        this.markersArray.forEach((marker) => {
          marker.setMap(this.map);
        });
      }
    },

    async addMultipleMarkersWithClustering(coordinateList) {
      await this.removeAllMarkers();
      if (coordinateList.length) {
        coordinateList.forEach((coordinates) => {
          const marker = this.addMarker(coordinates);
          this.markersArray.push(marker);
        });

        if (this.enableClustering && this.markerClusterer) {
          this.$nextTick(() => {
            this.markerClusterer.addMarkers(this.markersArray);
          });
        }

        this.fitBoundsToMarkers();
      }
    },

    addColoredMarkers(markersData) {
      const coloredMarkers = markersData.map((marker, index) => {
        const colors = Object.keys(this.markerColors);
        const randomColor = colors[index % colors.length];

        return {
          ...marker,
          color: marker.color || randomColor,
        };
      });

      this.updateMarkers(coloredMarkers);
    },
  },
  async mounted() {
    await this.initMap();
    document.addEventListener('fullscreenchange', this.handleFullscreenChange);
    document.addEventListener('webkitfullscreenchange', this.handleFullscreenChange);
    document.addEventListener('mozfullscreenchange', this.handleFullscreenChange);
    document.addEventListener('MSFullscreenChange', this.handleFullscreenChange);

    let interval = setInterval(() => {
      if (this.map && window.google && window.google.maps) {
        clearInterval(interval);
        this.map.data.addListener("addfeature", (e) => {
          this.bounds = new google.maps.LatLngBounds();
          this.processPoints(
            e.feature.getGeometry(),
            this.bounds.extend,
            this.bounds
          );
          this.map.setCenter(this.bounds.getCenter());
          this.map.fitBounds(this.bounds);
        });
      }
    }, 300);
  },

  beforeDestroy() {
    if (this.markerClusterer) {
      this.markerClusterer.clearMarkers();
    }
    document.removeEventListener('fullscreenchange', this.handleFullscreenChange);
    document.removeEventListener('webkitfullscreenchange', this.handleFullscreenChange);
    document.removeEventListener('mozfullscreenchange', this.handleFullscreenChange);
    document.removeEventListener('MSFullscreenChange', this.handleFullscreenChange);
  },
};
</script>




<style scoped>
.mapInfo {
  left: 5px;
  top: 5px;
  position: absolute;
  z-index: 1;
}
.fullscreen-toggle {
  right: 15px;
  top: 15px;
  position: absolute;
  z-index: 1;
}
</style>
<style>
.enhanced-info-window {
  font-family: "Segoe UI", Tahoma, Geneva, Verdana, sans-serif;
  min-width: 280px;
  max-width: 380px;
  padding: 0;
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 8px 25px rgba(0, 0, 0, 0.15);
}

.info-header {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  padding: 16px 20px;
  margin: -8px -8px 0 -8px;
  position: relative;
}

.header-content {
  padding-right: 40px;
}

.info-title {
  margin: 0;
  font-size: 18px;
  font-weight: 600;
  text-shadow: 0 1px 2px rgba(0, 0, 0, 0.1);
}

.trip-id {
  font-size: 12px;
  opacity: 0.9;
  margin-top: 4px;
  padding: 4px 8px;
  background: rgba(255, 255, 255, 0.15);
  border-radius: 6px;
  display: inline-block;
  font-weight: 500;
  letter-spacing: 0.5px;
}

.close-button {
  position: absolute;
  top: 50%;
  right: 12px;
  transform: translateY(-50%);
  background: rgba(255, 255, 255, 0.2);
  border: none;
  border-radius: 50%;
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.2s ease;
  backdrop-filter: blur(10px);
  color: white;
}

.close-button:hover {
  background: rgba(255, 255, 255, 0.3);
  transform: translateY(-50%) scale(1.1);
}

.close-button:active {
  transform: translateY(-50%) scale(0.95);
}

.truck-badge {
  position: absolute;
  top: 8px;
  right: 50px;
  background: rgba(255, 255, 255, 0.2);
  padding: 4px 8px;
  border-radius: 12px;
  font-size: 11px;
  backdrop-filter: blur(10px);
}

.info-content-scrollable {
  max-height: 300px;
  overflow-y: auto;
  overflow-x: hidden;
  background: white;
}

.info-content-scrollable::-webkit-scrollbar {
  width: 8px;
}

.info-content-scrollable::-webkit-scrollbar-track {
  background: #f1f1f1;
  border-radius: 4px;
}

.info-content-scrollable::-webkit-scrollbar-thumb {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  border-radius: 4px;
}

.info-content-scrollable::-webkit-scrollbar-thumb:hover {
  background: linear-gradient(135deg, #764ba2 0%, #667eea 100%);
}

.info-content-scrollable {
  scrollbar-width: thin;
  scrollbar-color: #667eea #f1f1f1;
}

.info-content {
  padding: 20px;
  background: white;
}

.detail-row {
  display: flex;
  align-items: center;
  padding: 12px 0;
  border-bottom: 1px solid #f0f0f0;
  transition: all 0.2s ease;
}

.detail-row:last-child {
  border-bottom: none;
  padding-bottom: 0;
}

.detail-row:hover {
  background-color: #f8f9fa;
  margin: 0 -12px;
  padding-left: 12px;
  padding-right: 12px;
  border-radius: 8px;
}

.detail-icon {
  font-size: 20px;
  margin-right: 12px;
  min-width: 24px;
  text-align: center;
}

.detail-content {
  flex: 1;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.detail-label {
  font-weight: 500;
  color: #555;
  font-size: 14px;
}

.detail-value {
  font-weight: 700;
  font-size: 15px;
  padding: 4px 8px;
  background-color: rgba(0, 0, 0, 0.04);
  border-radius: 6px;
  min-width: 60px;
  text-align: center;
}

.info-footer {
  background-color: #f8f9fa;
  padding: 12px 20px;
  border-top: 1px solid #e9ecef;
  margin: 0 -8px -8px -8px;
}

.timestamp {
  color: #6c757d;
  font-size: 12px;
  font-style: italic;
}

.hover-info-window {
  background: rgba(0, 0, 0, 0.8);
  color: white;
  padding: 12px 16px;
  border-radius: 8px;
  text-align: center;
  backdrop-filter: blur(10px);
}

.hover-title {
  font-weight: 600;
  font-size: 14px;
  margin-bottom: 4px;
}

.hover-speed {
  font-size: 12px;
  opacity: 0.9;
}

.gm-ui-hover-effect {
  display: none !important;
}

.gm-style .gm-style-iw-c {
  padding: 8px !important;
  border-radius: 12px !important;
  box-shadow: 0 8px 25px rgba(0, 0, 0, 0.15) !important;
}

.gm-style .gm-style-iw-d {
  overflow: hidden !important;
}
</style>
